import { useCallback, useMemo, useState } from "react";
import { useNavigate, useOutlet } from "react-router-dom";

import type { FoodItem } from "./food-item-combobox";

export type LogMealsOutletContext = {
  initialFoodName: string;
  onFoodCreated: (item: FoodItem) => void;
  onCancel: () => void;
};

type UseNewFoodFlowParams = {
  onStartDraft: (name: string) => void;
  onFoodCreated: (item: FoodItem) => void;
  onCancel: () => void;
};

export type UseNewFoodFlowResult = {
  outlet: ReturnType<typeof useOutlet>;
  isActive: boolean;
  newFoodDraftName: string | null;
  startCreateFlow: (name: string) => void;
};

export const useNewFoodFlow = ({ onStartDraft, onFoodCreated, onCancel }: UseNewFoodFlowParams): UseNewFoodFlowResult => {
  const navigate = useNavigate();
  const [newFoodDraftName, setNewFoodDraftName] = useState<string | null>(null);

  const handleFoodCreated = useCallback(
    (item: FoodItem) => {
      setNewFoodDraftName(null);
      onFoodCreated(item);
    },
    [onFoodCreated],
  );

  const handleCancel = useCallback(() => {
    setNewFoodDraftName(null);
    onCancel();
  }, [onCancel]);

  const outletContext = useMemo<LogMealsOutletContext>(
    () => ({
      initialFoodName: newFoodDraftName ?? "",
      onFoodCreated: handleFoodCreated,
      onCancel: handleCancel,
    }),
    [handleCancel, handleFoodCreated, newFoodDraftName],
  );

  const outlet = useOutlet(outletContext);

  const startCreateFlow = useCallback(
    (name: string) => {
      setNewFoodDraftName(name);
      onStartDraft(name);
      navigate("new-food", { state: { initialName: name } });
    },
    [navigate, onStartDraft],
  );

  return {
    outlet,
    isActive: Boolean(outlet),
    newFoodDraftName,
    startCreateFlow,
  };
};
